
import { useEffect, useRef } from 'react';

const IntelligentOptimization = () => {
  const visualizationRef = useRef<HTMLDivElement>(null);
  const geometricObjectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visualizationRef.current) return;

    const visualization = visualizationRef.current;
    const orbits = visualization.querySelectorAll('.orbit');
    
    // Create particles on orbits
    orbits.forEach((orbit, orbitIndex) => {
      const orbitSize = 80 + orbitIndex * 60;
      const particleCount = 3 + orbitIndex;
      
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2;
        const particle = document.createElement('div');
        particle.className = 'particle';
        
        // Set particle position
        const x = Math.cos(angle) * (orbitSize / 2);
        const y = Math.sin(angle) * (orbitSize / 2);
        
        particle.style.left = `${x + orbitSize / 2}px`;
        particle.style.top = `${y + orbitSize / 2}px`;
        
        // Store original position for animation
        particle.dataset.originX = x.toString();
        particle.dataset.originY = y.toString();
        particle.dataset.orbitSize = orbitSize.toString();
        particle.dataset.speed = (0.5 + Math.random() * 0.5).toString();
        particle.dataset.startAngle = angle.toString();
        
        orbit.appendChild(particle);
      }
    });
    
    // Create data points scattered around
    for (let i = 0; i < 50; i++) {
      const dataPoint = document.createElement('div');
      dataPoint.className = 'data-point';
      
      // Random position within visualization
      const x = Math.random() * visualization.offsetWidth;
      const y = Math.random() * visualization.offsetHeight;
      
      dataPoint.style.left = `${x}px`;
      dataPoint.style.top = `${y}px`;
      
      // Vary the size slightly
      const size = 3 + Math.random() * 5;
      dataPoint.style.width = `${size}px`;
      dataPoint.style.height = `${size}px`;
      
      // Vary the opacity
      dataPoint.style.opacity = (0.3 + Math.random() * 0.7).toString();
      
      visualization.appendChild(dataPoint);
    }
    
    // Create optimization beams
    for (let i = 0; i < 3; i++) {
      const beam = document.createElement('div');
      beam.className = 'optimization-beam';
      
      // Set beam size
      const size = 100 + i * 100;
      beam.style.width = `${size}px`;
      beam.style.height = `${size}px`;
      
      // Position at center
      beam.style.left = '50%';
      beam.style.top = '50%';
      
      // Store for animation
      beam.dataset.size = size.toString();
      beam.dataset.pulseSpeed = (3 + i).toString();
      
      visualization.appendChild(beam);
    }
    
    // Create trend lines
    for (let i = 0; i < 5; i++) {
      const trendLine = document.createElement('div');
      trendLine.className = 'trend-line';
      
      // Random position and angle
      const startX = Math.random() * visualization.offsetWidth * 0.3;
      const startY = 50 + Math.random() * (visualization.offsetHeight - 100);
      const length = 100 + Math.random() * 200;
      
      trendLine.style.left = `${startX}px`;
      trendLine.style.top = `${startY}px`;
      trendLine.style.width = `${length}px`;
      
      // Random angle
      const angle = -10 + Math.random() * 20;
      trendLine.style.transform = `rotate(${angle}deg)`;
      
      // Store for animation
      trendLine.dataset.startX = startX.toString();
      trendLine.dataset.length = length.toString();
      trendLine.dataset.speed = (0.5 + Math.random() * 1.5).toString();
      
      visualization.appendChild(trendLine);
    }
    
    // Animation loop
    let time = 0;
    
    function animate() {
      time += 0.01;
      
      // Animate particles
      document.querySelectorAll('.particle').forEach(particle => {
        const element = particle as HTMLElement;
        const orbitSize = parseFloat(element.dataset.orbitSize || "0");
        const speed = parseFloat(element.dataset.speed || "0");
        const startAngle = parseFloat(element.dataset.startAngle || "0");
        
        const angle = startAngle + time * speed;
        const x = Math.cos(angle) * (orbitSize / 2);
        const y = Math.sin(angle) * (orbitSize / 2);
        
        element.style.left = `${x + orbitSize / 2}px`;
        element.style.top = `${y + orbitSize / 2}px`;
      });
      
      // Animate optimization beams
      document.querySelectorAll('.optimization-beam').forEach(beam => {
        const element = beam as HTMLElement;
        const size = parseFloat(element.dataset.size || "0");
        const pulseSpeed = parseFloat(element.dataset.pulseSpeed || "0");
        
        // Pulse effect
        const pulseFactor = 1 + 0.1 * Math.sin(time * pulseSpeed);
        element.style.width = `${size * pulseFactor}px`;
        element.style.height = `${size * pulseFactor}px`;
        
        // Rotate
        element.style.transform = `translate(-50%, -50%) rotate(${time * 10}deg)`;
      });
      
      // Animate trend lines
      document.querySelectorAll('.trend-line').forEach(line => {
        const element = line as HTMLElement;
        const startX = parseFloat(element.dataset.startX || "0");
        const length = parseFloat(element.dataset.length || "0");
        const speed = parseFloat(element.dataset.speed || "0");
        
        if (visualization) {
          // Move from left to right
          const x = (startX + time * 30 * speed) % (visualization.offsetWidth + length);
          element.style.left = `${x - length * 0.5}px`;
        }
      });
      
      // Rotate core
      const core = document.querySelector('.core');
      if (core) {
        const element = core as HTMLElement;
        element.style.transform = `translate(-50%, -50%) rotate(${time * 45}deg)`;
      }
      
      requestAnimationFrame(animate);
    }
    
    const animationId = requestAnimationFrame(animate);
    
    // Cleanup on unmount
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="container" style={{maxWidth: '800px', height: '400px'}}>
      <p className="subtitle text-[#a0cfe8] text-base mb-10 text-center max-w-[600px] mx-auto leading-normal">
        Munger automatically analyzes campaign data and discovers optimization opportunities, saving hours of manual analysis.
      </p>
      
      <div ref={visualizationRef} className="visualization relative w-full h-[250px] overflow-visible">
        <div ref={geometricObjectRef} className="geometric-object absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100px] h-[100px]">
          <div className="core absolute top-1/2 left-1/2 w-[40px] h-[40px] -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[rgba(105,240,174,0.8)] shadow-[0_0_20px_rgba(105,240,174,0.5)]"></div>
          <div className="orbit absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(255,255,255,0.3)]" style={{width: '80px', height: '80px'}}></div>
          <div className="orbit absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(255,255,255,0.3)]" style={{width: '140px', height: '140px'}}></div>
          <div className="orbit absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(255,255,255,0.3)]" style={{width: '200px', height: '200px'}}></div>
          <div className="pulse absolute top-1/2 left-1/2 w-[100px] h-[100px] rounded-full bg-[rgba(105,240,174,0.1)] -translate-x-1/2 -translate-y-1/2 scale-0 animate-[pulse_3s_infinite]"></div>
        </div>
      </div>
    </div>
  );
};

export default IntelligentOptimization;
